import { useState } from 'react';
import type { AnalyticsFilters, TimeRange } from '@/types/analytics';
import styles from './DashboardFilters.module.scss';

export interface DashboardFiltersProps {
  filters: AnalyticsFilters;
  onChange: (filters: Partial<AnalyticsFilters>) => void;
  showDailyKnowledgeToggle?: boolean;
}

/**
 * Dashboard Filters Component
 *
 * Provides time range selection, date pickers, and filter controls
 * for analytics dashboards.
 */
export function DashboardFilters({
  filters,
  onChange,
  showDailyKnowledgeToggle = false,
}: DashboardFiltersProps) {
  const [showCustomDates, setShowCustomDates] = useState(filters.timeRange === 'custom');

  const handleTimeRangeChange = (timeRange: TimeRange) => {
    setShowCustomDates(timeRange === 'custom');
    onChange({
      timeRange,
      startDate: undefined,
      endDate: undefined,
    });
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', value: string) => {
    onChange({
      timeRange: 'custom',
      [field]: value,
    });
  };

  const timeRangeOptions: { value: TimeRange; label: string }[] = [
    { value: 'last7', label: 'Last 7 Days' },
    { value: 'last30', label: 'Last 30 Days' },
    { value: 'allTime', label: 'All Time' },
    { value: 'daily', label: 'Daily Knowledge Only' },
    { value: 'custom', label: 'Custom Range' },
  ];

  return (
    <div className={styles.filters}>
      {/* Time Range Buttons */}
      <div className={styles.filters__group}>
        <label className={styles.filters__label}>Time Range</label>
        <div className={styles.filters__buttonGroup}>
          {timeRangeOptions.map((option) => (
            <button
              key={option.value}
              className={`${styles.filters__button} ${
                filters.timeRange === option.value ? styles['filters__button--active'] : ''
              }`}
              onClick={() => handleTimeRangeChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Date Range */}
      {showCustomDates && (
        <div className={styles.filters__group}>
          <label className={styles.filters__label}>Custom Date Range</label>
          <div className={styles.filters__dateRange}>
            <input
              type="date"
              className={styles.filters__dateInput}
              value={filters.startDate || ''}
              onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
              placeholder="Start Date"
            />
            <span className={styles.filters__dateSeparator}>to</span>
            <input
              type="date"
              className={styles.filters__dateInput}
              value={filters.endDate || ''}
              onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
              placeholder="End Date"
            />
          </div>
        </div>
      )}

      {/* Daily Knowledge Toggle */}
      {showDailyKnowledgeToggle && filters.timeRange !== 'daily' && (
        <div className={styles.filters__group}>
          <label className={styles.filters__toggleLabel}>
            <input
              type="checkbox"
              className={styles.filters__checkbox}
              checked={filters.isDailyKnowledge || false}
              onChange={(e) => onChange({ isDailyKnowledge: e.target.checked })}
            />
            <span className={styles.filters__toggleText}>Daily Knowledge Only</span>
          </label>
        </div>
      )}
    </div>
  );
}
